import React from 'react';
import { useField } from 'formik';
import { Input } from '@dashboard/components/ui/input';
import ClearFieldButton from '../ClearFieldButton';
import { cn } from '@dashboard/lib/utils';

const TextInput = ({
  disabled,
  className,
  inputClassName,
  name,
  onChange,
  onBlur,
  type,
  suffix,
  clearable,
  // Engine-only props: Form.js renders these itself and still spreads them
  // down, so they reach the DOM as unknown attributes unless dropped here.
  helpText: _helpText,
  optional: _optional,
  visible: _visible,
  tooltip: _tooltip,
  showCount: _showCount,
  ...props
}) => {
  const node = React.useRef();
  const [field] = useField(name);

  React.useEffect(() => {
    if (type !== 'number') return;
    const handleWheel = e => {
      e.preventDefault();
      e.stopPropagation();
      node.current?.blur();
    };
    const element = node.current;
    if (element) {
      element.addEventListener('wheel', handleWheel, { passive: false });
      return () => element.removeEventListener('wheel', handleWheel);
    }
  }, [type]);

  const handleChange = e => {
    field.onChange(e);
    onChange?.(e);
  };

  const handleBlur = e => {
    field.onBlur(e);
    onBlur?.(e);
  };

  const input = (
    <Input
      ref={node}
      {...props}
      type={type || 'text'}
      name={name}
      value={field.value ?? ''}
      onChange={handleChange}
      onBlur={handleBlur}
      className={cn(className, inputClassName, suffix && 'pr-12', clearable && 'pr-9')}
      disabled={disabled}
    />
  );

  if (!suffix && !clearable) return input;

  return (
    <div className="group/clear relative">
      {input}
      {suffix && (
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-400 dark:text-muted-foreground">
          {suffix}
        </span>
      )}
      {clearable && (
        <ClearFieldButton
          name={name}
          disabled={disabled}
          onClear={() => {
            onChange?.({ target: { name, value: '' } });
            node.current?.focus();
          }}
          className="inset-y-0 my-auto h-fit"
        />
      )}
    </div>
  );
};

export default TextInput;
